type TgUser = {
  id: number;
  is_bot?: boolean;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
};

type TgThemeParams = Partial<{
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
  header_bg_color: string;
  accent_text_color: string;
  section_bg_color: string;
  section_header_text_color: string;
  subtitle_text_color: string;
  destructive_text_color: string;
}>;

type TgWebApp = {
  ready: () => void;
  expand: () => void;
  close: () => void;
  platform: string;
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  initData: string;
  initDataUnsafe: { user?: TgUser; start_param?: string };
  MainButton: {
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  showPopup: (
    params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'; text?: string }> },
    callback?: (button_id: string | null) => void,
  ) => void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const getWebApp = (): TgWebApp | null => {
  if (typeof window !== "undefined" && window.Telegram?.WebApp) return window.Telegram.WebApp;
  return null;
};

export const getThemeParams = (): TgThemeParams => getWebApp()?.themeParams || {};
export const getTgUser = (): TgUser | null => getWebApp()?.initDataUnsafe?.user ?? null;

export const initTelegram = (): void => {
  const webApp = getWebApp();
  if (webApp) {
    webApp.ready();
    webApp.expand();
    try {
      webApp.MainButton.setParams({ color: '#3390EC', text_color: '#ffffff' });
    } catch {
      // older clients
    }
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('gesturechange', (e) => e.preventDefault());
    document.addEventListener('gestureend', (e) => e.preventDefault());
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    }, { passive: false });
  }
};

export const haptic = (kind: 'light' | 'success' | 'warning' | 'error' = 'light'): void => {
  const tg = getWebApp();
  if (!tg?.HapticFeedback) return;
  if (kind === 'light') tg.HapticFeedback.impactOccurred('light');
  else tg.HapticFeedback.notificationOccurred(kind);
};

/**
 * Promise-обёртка над `tg.showPopup` для destructive-подтверждений в админке.
 * resolve(true) если юзер нажал «Подтвердить», иначе false. В dev-браузере без TG SDK
 * fallback на `window.confirm`.
 */
export const confirmAction = (message: string, title?: string): Promise<boolean> => {
  const tg = getWebApp();
  if (!tg?.showPopup) {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      return Promise.resolve(window.confirm(message));
    }
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    try {
      tg.showPopup(
        {
          title,
          message,
          buttons: [
            { id: 'ok', type: 'destructive', text: 'Подтвердить' },
            { id: 'cancel', type: 'cancel' },
          ],
        },
        (button_id) => resolve(button_id === 'ok'),
      );
    } catch {
      resolve(false);
    }
  });
};
